import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Utensils, Apple, Carrot, Egg, Fish } from 'lucide-react';
import toast from 'react-hot-toast';
import api from "services/api";

// Main MealPlan component that combines all sub-components
const CreateMealPlan = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [preferences, setPreferences] = useState({
    carbs: [],
    proteins: [],
    fats: [],
    fruits_vegetables: [],
    meals_per_day: 3,
    dietary_requirements: [],
    additional_info: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  const steps = [
    { component: Carbs, title: "Carbohydrates", validate: Carbs.validate },
    { component: Proteins, title: "Proteins", validate: Proteins.validate },
    { component: Fats, title: "Healthy Fats", validate: Fats.validate },
    { component: FruitsAndVegetables, title: "Fruits & Vegetables", validate: FruitsAndVegetables.validate },
    { component: Preferences, title: "Additional Preferences", validate: Preferences.validate }
  ];

  const handleNext = async (e) => {
    e.preventDefault();
    const error = steps[currentStep - 1].validate(preferences);
    if (error) {
      toast.error(error);
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        setIsLoading(true);
        const response = await api.post('/meal-plans/create', { preferences });
        if (!response.ok) {
          toast.error("Failed to generate meal plan");
          return;
        }
        toast.success('Meal plan generated successfully!');
        navigate('/meal-dashboard');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to generate meal plan');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-black text-center">Create Your Meal Plan</h1>
          <div className="mt-4 text-center text-sm text-black">
            Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
          </div>
        </div>

        <form onSubmit={handleNext} className="space-y-6">
          <div className="mb-8">
            <CurrentStepComponent
              preferences={preferences}
              onPreferencesChange={(newPrefs) => setPreferences({ ...preferences, ...newPrefs })}
            />
          </div>

          <div className="flex justify-between mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 border border-black rounded-md shadow-sm text-sm font-medium text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
              >
                Previous
              </button>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="ml-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Generating...
                </div>
              ) : (
                currentStep === steps.length ? 'Generate Meal Plan' : 'Next'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Carbs Component - Allows users to select their preferred carbohydrate sources
const Carbs = ({ preferences, onPreferencesChange }) => {
  const carbOptions = [
    { id: 'quinoa', label: 'Quinoa', description: 'Ancient grain high in protein' },
    { id: 'sweet_potato', label: 'Sweet Potato', description: 'Rich in vitamins and fiber' },
    { id: 'oats', label: 'Oats', description: 'Heart-healthy breakfast option' },
    { id: 'brown_rice', label: 'Brown Rice', description: 'Whole grain alternative to white rice' },
    { id: 'whole_wheat_bread', label: 'Whole Wheat Bread', description: 'Nutritious bread option' },
    { id: 'pasta', label: 'Pasta', description: 'Italian classic' },
    { id: 'barley', label: 'Barley', description: 'Ancient whole grain' },
    { id: 'buckwheat', label: 'Buckwheat', description: 'Gluten-free pseudo-grain' }
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Utensils className="mr-2" /> Choose Your Carbs
      </h2>
      <p className="text-sm text-black">Select your preferred carbohydrate sources</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {carbOptions.map(carb => (
          <div
            key={carb.id}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              preferences.carbs.includes(carb.id)
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-gray-400'
            }`}
            onClick={() => {
              const newCarbs = preferences.carbs.includes(carb.id)
                ? preferences.carbs.filter(c => c !== carb.id)
                : [...preferences.carbs, carb.id];
              onPreferencesChange({ carbs: newCarbs });
            }}
          >
            <h3 className="font-medium text-black">{carb.label}</h3>
            <p className="text-sm text-gray-500 mt-1 hidden sm:block">{carb.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

Carbs.validate = (preferences) => {
  if (!preferences.carbs || preferences.carbs.length === 0) {
    return 'Select at least one carbohydrate source';
  }
  return null;
};

// Proteins Component - Allows users to select their preferred protein sources
const Proteins = ({ preferences, onPreferencesChange }) => {
  const proteinOptions = [
    { id: 'chicken', label: 'Chicken', description: 'Lean white meat protein' },
    { id: 'fish', label: 'Fish', description: 'Rich in omega-3 fatty acids' },
    { id: 'eggs', label: 'Eggs', description: 'Complete protein source' },
    { id: 'tofu', label: 'Tofu', description: 'Plant-based protein alternative' },
    { id: 'turkey', label: 'Turkey', description: 'Lean poultry option' },
    { id: 'beef', label: 'Beef', description: 'Red meat rich in iron' },
    { id: 'tempeh', label: 'Tempeh', description: 'Fermented soy protein' },
    { id: 'greek_yogurt', label: 'Greek Yogurt', description: 'High-protein dairy option' },
    { id: 'cottage_cheese', label: 'Cottage Cheese', description: 'Low-fat dairy protein' }
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Egg className="mr-2" /> Choose Your Proteins
      </h2>
      <p className="text-sm text-black">Select your preferred protein sources</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {proteinOptions.map(protein => (
          <div
            key={protein.id}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              preferences.proteins.includes(protein.id)
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-gray-400'
            }`}
            onClick={() => {
              const newProteins = preferences.proteins.includes(protein.id)
                ? preferences.proteins.filter(p => p !== protein.id)
                : [...preferences.proteins, protein.id];
              onPreferencesChange({ proteins: newProteins });
            }}
          >
            <h3 className="font-medium text-black">{protein.label}</h3>
            <p className="text-sm text-gray-500 mt-1 hidden sm:block">{protein.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

Proteins.validate = (preferences) => {
  if (!preferences.proteins || preferences.proteins.length === 0) {
    return 'Select at least one protein source';
  }
  return null;
};

// Fats Component - Allows users to select their preferred healthy fat sources
const Fats = ({ preferences, onPreferencesChange }) => {
  const fatOptions = [
    { id: 'avocado', label: 'Avocado', description: 'Rich in healthy monounsaturated fats' },
    { id: 'olive_oil', label: 'Olive Oil', description: 'Heart-healthy cooking oil' },
    { id: 'nuts', label: 'Nuts', description: 'Various nuts for healthy fats' },
    { id: 'seeds', label: 'Seeds', description: 'Nutrient-dense seed options' },
    { id: 'coconut_oil', label: 'Coconut Oil', description: 'Medium-chain triglycerides' },
    { id: 'chia_seeds', label: 'Chia Seeds', description: 'Omega-3 rich seeds' },
    { id: 'flax_seeds', label: 'Flax Seeds', description: 'High in lignans and omega-3' },
    { id: 'almonds', label: 'Almonds', description: 'Vitamin E rich nuts' },
    { id: 'walnuts', label: 'Walnuts', description: 'Brain-healthy omega-3 fats' }
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Fish className="mr-2" /> Choose Your Healthy Fats
      </h2>
      <p className="text-sm text-black">Select your preferred healthy fat sources</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fatOptions.map(fat => (
          <div
            key={fat.id}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              preferences.fats.includes(fat.id)
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-gray-400'
            }`}
            onClick={() => {
              const newFats = preferences.fats.includes(fat.id)
                ? preferences.fats.filter(f => f !== fat.id)
                : [...preferences.fats, fat.id];
              onPreferencesChange({ fats: newFats });
            }}
          >
            <h3 className="font-medium text-black">{fat.label}</h3>
            <p className="text-sm text-gray-500 mt-1 hidden sm:block">{fat.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

Fats.validate = (preferences) => {
  if (!preferences.fats || preferences.fats.length === 0) {
    return 'Select at least one healthy fat source';
  }
  return null;
};

// Fruits and Vegetables Component - Allows users to select their preferred fruits and vegetables
const FruitsAndVegetables = ({ preferences, onPreferencesChange }) => {
  const options = [
    { id: 'spinach', label: 'Spinach', description: 'Iron-rich leafy green' },
    { id: 'broccoli', label: 'Broccoli', description: 'Nutrient-dense cruciferous vegetable' },
    { id: 'berries', label: 'Berries', description: 'Antioxidant-rich fruits' },
    { id: 'banana', label: 'Banana', description: 'Potassium-rich fruit' },
    { id: 'kale', label: 'Kale', description: 'Super nutritious leafy green' },
    { id: 'sweet_peppers', label: 'Bell Peppers', description: 'Vitamin C rich vegetables' },
    { id: 'carrots', label: 'Carrots', description: 'Beta-carotene rich vegetable' },
    { id: 'apples', label: 'Apples', description: 'Fiber-rich fruit' },
    { id: 'oranges', label: 'Oranges', description: 'Vitamin C rich citrus' },
    { id: 'tomatoes', label: 'Tomatoes', description: 'Lycopene-rich fruit/vegetable' }
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Carrot className="mr-2" /> Choose Your Fruits & Vegetables
      </h2>
      <p className="text-sm text-black">Select your preferred fruits and vegetables</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {options.map(item => (
          <div
            key={item.id}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              preferences.fruits_vegetables.includes(item.id)
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-gray-400'
            }`}
            onClick={() => {
              const newItems = preferences.fruits_vegetables.includes(item.id)
                ? preferences.fruits_vegetables.filter(f => f !== item.id)
                : [...preferences.fruits_vegetables, item.id];
              onPreferencesChange({ fruits_vegetables: newItems });
            }}
          >
            <h3 className="font-medium text-black">{item.label}</h3>
            <p className="text-sm text-gray-500 mt-1 hidden sm:block">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

FruitsAndVegetables.validate = (preferences) => {
  if (!preferences.fruits_vegetables || preferences.fruits_vegetables.length === 0) {
    return 'Select at least one fruit or vegetable';
  }
  return null;
};

// Preferences Component - Allows users to set additional preferences
const Preferences = ({ preferences, onPreferencesChange }) => {
  const dietaryOptions = [
    { id: 'halal', label: 'Halal', description: 'Follows Islamic dietary laws' },
    { id: 'kosher', label: 'Kosher', description: 'Follows Jewish dietary laws' },
    { id: 'vegetarian', label: 'Vegetarian', description: 'No meat products' },
    { id: 'pescatarian', label: 'Pescatarian', description: 'Fish but no other meat' },
    { id: 'dairy_free', label: 'Dairy Free', description: 'No dairy products' },
    { id: 'gluten_free', label: 'Gluten Free', description: 'No gluten-containing grains' },
  ];

  const handleChange = (field, value) => {
    onPreferencesChange({ [field]: value });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Apple className="mr-2" /> Additional Preferences
      </h2>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium text-black">
          Meals Per Day
        </label>
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => handleChange('meals_per_day', Math.max(2, preferences.meals_per_day - 1))}
            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            <Minus className="text-black" />
          </button>
          <span className="text-xl font-semibold text-black">{preferences.meals_per_day}</span>
          <button
            type="button"
            onClick={() => handleChange('meals_per_day', Math.min(6, preferences.meals_per_day + 1))}
            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            <Plus className="text-black" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-black">
          Dietary Requirements
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dietaryOptions.map(diet => (
            <div
              key={diet.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                preferences.dietary_requirements.includes(diet.id)
                  ? 'border-black bg-gray-50'
                  : 'border-gray-200 hover:border-gray-400'
              }`}
              onClick={() => {
                const newDiets = preferences.dietary_requirements.includes(diet.id)
                  ? preferences.dietary_requirements.filter(d => d !== diet.id)
                  : [...preferences.dietary_requirements, diet.id];
                handleChange('dietary_requirements', newDiets);
              }}
            >
              <h3 className="font-medium text-black">{diet.label}</h3>
              <p className="text-sm text-gray-500 mt-1 hidden sm:block">{diet.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-black">
          Additional Information
        </label>
        <textarea
          value={preferences.additional_info}
          onChange={(e) => handleChange('additional_info', e.target.value)}
          placeholder="Any additional preferences or requirements..."
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black"
        />
      </div>
    </div>
  );
};

Preferences.validate = (preferences) => {
  if (!preferences.meals_per_day || preferences.meals_per_day < 2 || preferences.meals_per_day > 6) {
    return 'Meals per day must be between 2 and 6';
  }
  return null;
};

export default CreateMealPlan;